import { useState } from 'react';

import { DutyCode } from 'generated';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { queries, requests, queryKeys } from 'RestAPIs/rapini';

import { GridColDef, GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';

import { AdditionalButton } from 'components/shared/DataGrid/DataGrid.types';
import AddDutyCode from 'components/shared/DutyCodesGrid/AddDutyCode/components/AddDutyCode';
import AddPersonalizedDutyCode from 'components/shared/DutyCodesGrid/AddPersonalizedDutyCode/components/AddPersonalizedDutyCode';
import EditDutyCode from 'components/shared/DutyCodesGrid/EditDutyCode/components/EditDutyCode';
import EditPersonalizedDutyCode from 'components/shared/DutyCodesGrid/EditPersonalizedDutyCode/components/EditPersonalizedDutyCode';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import showSnackbar from 'components/shared/Snackbar';

import { DutyCodePeriods } from '../DataGrid/components/CustomCellRenderer/DutyCodePeriods';

import {
  SharedProps,
  withDutyCodeSharedProps
} from './withDutyCodeSharedProps';
import { dateFormatter } from '../DataGrid/utils/valueFormatters/dateFormatter';
import ConfirmationDialog from '../ConfirmationDialog';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

// types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

const { deleteDutyCode } = requests;

type DutyCodesGridProps = {
  referenceDate?: Date;
  organizationalUnitId?: number;
} & SharedProps;

const DutyCodesGrid = ({
  referenceDate,
  organizationalUnitId,
  sx,
  getRowHeight
}: DutyCodesGridProps) => {
  const [shouldShowAddDutyCodeDialog, setShouldShowAddDutyCodeDialog] =
    useState<boolean>(false);
  const [shouldShowEditDutyCodeDialog, setShouldShowEditDutyCodeDialog] =
    useState<boolean>(false);
  const [
    shouldShowAddPersonalizedDutyDialog,
    setShouldShowAddPersonalizedDutyCodeDialog
  ] = useState<boolean>(false);
  const [
    shouldShowEditPersonalizedDutyDialog,
    setShouldShowEditPersonalizedDutyCodeDialog
  ] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState<GridRowId | null>(null);

  const [addEditDialogData, setAddEditDialogData] = useState<DutyCode | null>(
    null
  );
  const { t } = useTranslation();
  const { organisationPreferences } = useSelector((state) => state);
  const { settings: orgSettings } = organisationPreferences;

  const { data: dutyCodes } = queries.useGetDutyCodes(
    referenceDate?.toISOString(),
    organizationalUnitId
  );

  function getDutyCode(dutyCode: DutyCode) {
    return dutyCode.organizationalUnitId
      ? dutyCode.code || dutyCode.dutyCode
      : dutyCode.code
      ? `${dutyCode.code} (${dutyCode.dutyCode})`
      : dutyCode.dutyCode;
  }

  const columns: GridColDef<GridValidRowModel>[] = [
    {
      field: 'dutyCodeDisplay',
      headerName: t('dutyCode'),
      flex: 1,
      minWidth: 80
    },
    {
      field: 'colorCode',
      headerName: t('color'),
      renderCell: ({ value }) => (
        <div
          style={{
            width: 16,
            height: 16,
            backgroundColor: value,
            borderRadius: 1,
            border: '1px solid #cccccc'
          }}
        />
      ),
      flex: 1
    },
    {
      field: 'startTimeDisplay',
      headerName: t('validFrom'),
      flex: 1,
      minWidth: 100,
      valueFormatter: dateFormatter
    },
    {
      field: 'endTimeDisplay',
      headerName: t('validTo'),
      flex: 1,
      minWidth: 100,
      valueFormatter: dateFormatter
    },
    {
      field: 'periods',
      headerName: t('periods'),
      minWidth: 300,
      renderCell: ({ value }) => {
        return <DutyCodePeriods periods={value} periodId="dutyCodePeriodId" />;
      },
      flex: 1
    }
  ];

  const rows = dutyCodes
    ? dutyCodes
        .filter((dutyCode) => dutyCode.code !== null)
        .map((dutyCode) => ({
          id: dutyCode.dutyCodeId,
          displayDutyCode: dutyCode.code,
          dutyCodeDisplay: getDutyCode(dutyCode),
          dutyCode: dutyCode.dutyCode,
          description: dutyCode.description,
          colorCode: dutyCode.colorCode,
          startTime: dutyCode.validFrom,
          startTimeDisplay: dutyCode?.validFrom ?? '-',
          endTime: dutyCode.validTo,
          endTimeDisplay: dutyCode?.validTo ?? '-',
          periods: dutyCode.periods,
          isDeleteDisabled: !!dutyCode.organizationalUnitId
        }))
    : [];

  function onClickAddDutyCode() {
    setShouldShowAddDutyCodeDialog(true);
  }

  function onClickEditDutyCode() {
    setShouldShowEditDutyCodeDialog(true);
  }

  function onClickAddPersonalizeDutyCode() {
    setShouldShowAddPersonalizedDutyCodeDialog(true);
  }

  function onClickEditPersonalizeDutyCode() {
    setShouldShowEditPersonalizedDutyCodeDialog(true);
  }

  function handleCloseAddEdit() {
    setShouldShowAddDutyCodeDialog(false);
    setShouldShowEditDutyCodeDialog(false);
    setShouldShowAddPersonalizedDutyCodeDialog(false);
    setShouldShowEditPersonalizedDutyCodeDialog(false);
    setAddEditDialogData(null);
  }

  function onSaveDutyCode() {
    handleCloseAddEdit();
  }

  const additionalButtons: AdditionalButton[] = [
    {
      label: t('addDutyCode'),
      onClick: onClickAddDutyCode,
      disabled: !orgSettings['decentralized.dutycodes.active'] || false
    },
    {
      label: t('addPersonalizedDutyCode'),
      onClick: onClickAddPersonalizeDutyCode
    }
  ];

  function onClickEditRow(id: number) {
    // TODO check Personalized vs Organizational Unit DutyCode
    const dutyCode = dutyCodes?.find(
      ({ dutyCodeId: cId }) => cId === id
    ) as DutyCode;
    setAddEditDialogData(dutyCode);
    if (dutyCode.organizationalUnitId) {
      onClickEditDutyCode();
    } else {
      onClickEditPersonalizeDutyCode();
    }
  }

  const QueryClient = useQueryClient();

  const handleDelete = (id: string | number) => {
    setRowIdToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const onDeleteConfirmation = (isConfirmed: boolean) => {
    if (isConfirmed && onDeleteRow) {
      if (rowIdToDelete) {
        onDeleteRow([rowIdToDelete]);
        setRowIdToDelete(null);
      }
    }
    setShowDeleteConfirmation(false);
  };

  function onDeleteRow(id: GridRowId[]) {
    setIsDeleting(true);
    deleteDutyCode({
      dutyCodeId: id[0] as number,
      organizationalUnitId
    }).then(
      () => {
        QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
        setIsDeleting(false);
      },
      () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
        setIsDeleting(false);
      }
    );
  }

  return (
    <>
      <TableCRUDLayout
        columns={columns}
        rows={rows}
        shouldRenderToolBar={false}
        renderFormContainer={false}
        hideCreateAction
        editable={false}
        additionalButtons={additionalButtons}
        onEditClick={(id) => onClickEditRow(+id)}
        getRowHeight={getRowHeight}
        sx={sx}
        dynamicHeight={true}
        customActions={(id, row) => [
          ...(!row?.isDeleteDisabled
            ? [
                {
                  tooltipTitle: t('delete'),
                  icon: 'trash-can' as IconType,
                  onClick: () => handleDelete(id)
                }
              ]
            : [])
        ]}
      />
      <AddDutyCode
        isOpen={shouldShowAddDutyCodeDialog}
        handleClose={handleCloseAddEdit}
        onSave={onSaveDutyCode}
        organizationalUnitId={organizationalUnitId}
      />
      {addEditDialogData && (
        <EditDutyCode
          isOpen={shouldShowEditDutyCodeDialog}
          dutyCode={addEditDialogData}
          handleClose={handleCloseAddEdit}
          onSave={onSaveDutyCode}
          organizationalUnitId={organizationalUnitId}
        />
      )}
      {organizationalUnitId && (
        <AddPersonalizedDutyCode
          isOpen={shouldShowAddPersonalizedDutyDialog}
          handleClose={handleCloseAddEdit}
          onSave={onSaveDutyCode}
          organizationalUnitId={organizationalUnitId}
        />
      )}
      {organizationalUnitId && addEditDialogData && (
        <EditPersonalizedDutyCode
          isOpen={shouldShowEditPersonalizedDutyDialog}
          dutyCode={addEditDialogData}
          handleClose={handleCloseAddEdit}
          onSave={onSaveDutyCode}
          organizationalUnitId={organizationalUnitId}
        />
      )}
      <LoadingOverlay loading={isDeleting} />

      {showDeleteConfirmation && (
        <ConfirmationDialog
          handleAction={onDeleteConfirmation}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={t('dutyCodes.confirmationTitle')}
        >
          <p>{t('dutyCodes.confirmationBody')}</p>
        </ConfirmationDialog>
      )}
    </>
  );
};

const DutyCodesGridWithSharedProps = withDutyCodeSharedProps(DutyCodesGrid);
export default DutyCodesGridWithSharedProps;
